<template>
	<EventLayout :event="event" :subtitle="stage.title" :return-url="route('events.stages.registration.index', event.id)" :return-label="trans('events.stages.registration.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="update">
				<i class="fa-solid fa-save mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<EditStage :stage="stage">
			<template #switches>
				<Switch v-model="stage.refundable" :label="trans('events.prices.refundable')"/>
				<Switch v-model="stage.show_availability" :label="trans('events.prices.show_availability')"/>
			</template>
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
				<Card :title="trans('events.stages.shopping.restrictions.title')" :subtitle="trans('events.stages.shopping.restrictions.description')">
					<div class="grid grid-cols-2 gap-3">
						<Switch v-model="stage.general_enabled" :label="trans('account.types.general')"/>
						<Switch v-model="stage.local_enabled" :label="trans('account.types.local')"/>
						<Switch v-model="stage.international_enabled" :label="trans('account.types.international')"/>
						<Switch v-model="stage.representative_enabled" :label="trans('account.types.representative')"/>
						<Switch v-model="stage.organizer_enabled" :label="trans('account.types.organizer')"/>
						<Switch v-model="stage.workgroup_enabled" :label="trans('account.types.workgroup')"/>
						<Switch v-model="stage.board_enabled" :label="trans('account.types.board')"/>
						<Switch v-model="stage.dinosaur_enabled" :label="trans('account.types.dinosaur')"/>
						<Switch v-model="stage.early_enabled" :label="trans('account.types.early')"/>
					</div>
					<LabeledObject :description="trans('events.prices.payment_timeout.description')">
						<template #label>{{ trans('events.prices.payment_timeout.title') }}</template>
						<div class="grow flex gap-2">
							<Switch v-model="showPaymentTimeout" :label="trans(showPaymentTimeout ? 'general.enabled' : 'general.disabled')"/>
							<IconNumberInput v-if="showPaymentTimeout" v-model="stage.payment_timeout" icon-class="fa-solid fa-clock" :min="0" :max="4294967295" :step="1" full/>
						</div>
					</LabeledObject>
				</Card>
				<Card :title="trans('events.stages.shopping.bundles.title')" :subtitle="trans('events.stages.shopping.bundles.subtitle')">
					<List>
						<ListItem v-for="bundle in bundles" :title="bundle.title" @click.stop="toggleBundle(bundle.id)">
							<i v-if="selectedBundleIds.includes(bundle.id)" class="fa-solid fa-toggle-on text-green-600"/>
							<i v-else class="fa-solid fa-toggle-off text-red-600"/>
						</ListItem>
					</List>
				</Card>
			</div>
		</EditStage>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {type Event} from '@/Types/Events/Event'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import {defineProps, type Ref, ref, watch} from 'vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import {type Bundle} from '@/Types/Events/Bundles/Bundle'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import EditStage from '@/Components/Events/Stages/EditStage.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import IconNumberInput from '@/Components/UI/Inputs/IconNumberInput.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type RegistrationStage} from '@/Types/Events/Stages/RegistrationStage'

const props = defineProps<{
	event:Event,
	stage:RegistrationStage,
	bundles:Array<Bundle>,
	selectedBundleIds:Array<number>,
}>()

const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const stage:Ref<RegistrationStage> = ref(props.stage)
const bundles:Ref<Array<Bundle>> = ref(props.bundles)
const selectedBundleIds:Ref<Array<number>> = ref(props.selectedBundleIds)
const showPaymentTimeout:Ref<number|null> = ref(stage.value.payment_timeout !== null)

watch(showPaymentTimeout, (value) => {
	stage.value.payment_timeout = value ? 0 : null
})

function update():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.stages.shopping.update', {
			event: event.value.id,
			stage: stage.value.id
		}), stage.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function toggleBundle(bundleId:number):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.stages.shopping.toggle', [event.value.id, stage.value.id, bundleId])).then(() => {
			showSuccess(trans('general.updated'))
			const index:number = selectedBundleIds.value.findIndex((id:number) => id === bundleId)
			if(index > -1){
				selectedBundleIds.value.splice(index, 1)
			} else {
				selectedBundleIds.value.push(bundleId)
			}
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>